import { CircleHalf, MagnifyingGlass, MoonStars, Plus, SunDim } from '@phosphor-icons/react';
import { useTheme } from '@/app/providers/theme-provider';
import { THEME_MODE_LABELS } from '@/design/themes/theme';
import { Button } from '@/components/primitives/button';
import { IconButton } from '@/components/primitives/icon-button';
import { SaveStatusPill } from '@/features/save-status/save-status-pill';
import { PlatformBadge } from '@/platform/platform-badge';
import type { SaveState } from '@/features/save-status/save-status';
import type { MirrorHealthSnapshot } from '@/data/mirror/mirror-health';

interface TopToolbarProps {
  title: string;
  purpose: string;
  onQuickCapture?: () => void;
  quickCaptureDisabled?: boolean;
  onSearch?: () => void;
  saveState?: SaveState;
  lastSavedAt?: string | null;
  onRetrySave?: () => void;
  mirror?: MirrorHealthSnapshot;
  onRetryMirror?: () => void;
}

export function TopToolbar({
  title,
  purpose,
  onQuickCapture,
  quickCaptureDisabled = false,
  onSearch,
  saveState = 'idle',
  lastSavedAt = null,
  onRetrySave,
  mirror,
  onRetryMirror,
}: TopToolbarProps) {
  const { mode, resolvedTheme, cycleMode } = useTheme();

  return (
    <header className="flex h-16 shrink-0 items-center gap-4 px-6">
      <div className="min-w-0 flex-1">
        <h1 className="truncate text-[1.375rem] leading-tight font-semibold tracking-tight">
          {title}
        </h1>
        <p className="truncate text-[0.8125rem] text-text-muted">{purpose}</p>
      </div>

      <div className="flex shrink-0 items-center gap-2">
        <PlatformBadge />
        <SaveStatusPill
          state={saveState}
          lastSavedAt={lastSavedAt}
          {...(onRetrySave ? { onRetry: onRetrySave } : {})}
          {...(mirror ? { mirror } : {})}
          {...(onRetryMirror ? { onRetryMirror } : {})}
        />
        <IconButton label="搜尋" icon={<MagnifyingGlass size={19} />} onClick={onSearch} />
        <IconButton
          label={`切換主題（目前：${THEME_MODE_LABELS[mode]}）`}
          icon={
            mode === 'system' ? (
              <CircleHalf size={19} />
            ) : resolvedTheme === 'dark' ? (
              <MoonStars size={19} />
            ) : (
              <SunDim size={19} />
            )
          }
          onClick={cycleMode}
        />
        <Button
          variant="primary"
          size="sm"
          className="shrink-0 whitespace-nowrap"
          onClick={onQuickCapture}
          disabled={quickCaptureDisabled}
          aria-label="快速新增"
          title={quickCaptureDisabled ? '請先建立或還原工作台' : undefined}
          trailingIcon={<Plus size={14} weight="bold" />}
        >
          快速新增
        </Button>
      </div>
    </header>
  );
}
